import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { DialogProvider } from './contexts/DialogContext';
import { GoogleMapsProvider } from './contexts/GoogleMapsContext';
import ConvexProvider from './providers/ConvexProvider';
import { BrandingProvider } from './components/common/BrandingProvider';
import RoleBasedRoute, { getDefaultRoute } from './components/common/RoleBasedRoute';
import Layout from './components/common/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Configuration from './pages/Configuration';
import Users from './pages/Users';
import RolesPermissions from './pages/RolesPermissions';
import CommonCodes from './pages/CommonCodes';
import CauseOfCalls from './pages/CauseOfCalls';
import Diagnostics from './pages/Diagnostics';
import CaseBooking from './pages/CaseBooking';
import ErrorBoundary from './components/common/ErrorBoundary';
import CreateCase from './pages/CreateCase';
import Services from './pages/Services';
import AddService from './pages/AddService';
import VendorSearch from './pages/VendorSearch';
import Invoices from './pages/Invoices';
import Referrals from './pages/Referrals';
import CaseHistory from './pages/CaseHistory';
import { useAuth } from './hooks/useAuth';
import NotificationHandler from './components/common/NotificationHandler';
import { useAgentPaymentAlerts } from './hooks/useAgentPaymentAlerts';
import ShareFeedbackPage from './pages/ShareFeedback';
import ShareMyLocation from './pages/ShareMyLocation';
import VendorTracking from './pages/VendorTracking';
import VendorRegistrations from './pages/VendorRegistrations';
import DealerMaintenance from './pages/DealerMaintenance';
import ClientServiceRates from './pages/ClientServiceRates';
import Regions from './pages/Regions';
import RevenueReport from './pages/RevenueReport';
import VendorSlaReport from './pages/VendorSlaReport';
import SchemeServiceSetup from './pages/SchemeServiceSetup';
import ServiceStatuses from './pages/ServiceStatuses';
import SmsTemplates from './pages/SmsTemplates';
import FirebaseTest from './components/FirebaseTest';
import VendorLocationsAdmin from './pages/VendorLocationsAdmin';
import VendorPerformance from './pages/VendorPerformance';
import VendorSubscriptions from './pages/VendorSubscriptions';
import DispatchQueue from './pages/DispatchQueue';
import DisputesQueue from './pages/DisputesQueue';
import { hydrateLocalizationDefaults } from './utils/hydrateLocalization';
// Component to handle role-based default routing
const DefaultRouteHandler = () => {
  const { user } = useAuth();
  const defaultRoute = getDefaultRoute(user?.role);
  return <Navigate to={defaultRoute} replace />;
};

function AgentPaymentAlertBridge() {
  useAgentPaymentAlerts();
  return null;
}

function App() {
  useEffect(() => {
    hydrateLocalizationDefaults();
  }, []);

  return (
    <ErrorBoundary>
      <BrandingProvider>
        <AuthProvider>
          <DialogProvider>
          <ConvexProvider>
          <GoogleMapsProvider>
            <Router>
            <NotificationHandler />
            <AgentPaymentAlertBridge />
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/shareFeedback" element={<ShareFeedbackPage />} />
              <Route path="/share-my-location" element={<ShareMyLocation />} />
              {/* Public live tracking route */}
              <Route path="/vendor-tracking/:serviceId" element={<VendorTracking />} />
              {/* Firebase test route */}
              <Route path="/firebase-test" element={<FirebaseTest />} />
              <Route
                path="/"
                element={
                  <RoleBasedRoute>
                    <Layout />
                  </RoleBasedRoute>
                }
              >
                {/* Default route - role-based redirect */}
                <Route index element={<DefaultRouteHandler />} />

                {/* All routes now use automatic permission checking */}
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="configuration" element={<Configuration />} />
                <Route path="case-booking" element={<CaseBooking />} />
                <Route path="create-case" element={<CreateCase />} />
                <Route path="services" element={<Services />} />
                <Route path="add-service" element={<AddService />} />
                <Route path="vendor-search" element={<VendorSearch />} />
                <Route path="invoices" element={<Invoices />} />
                <Route path="users" element={<Users />} />
                <Route path="referrals" element={<Referrals />} />
                <Route path="case-history" element={<CaseHistory />} />
                <Route path="vendor-registrations" element={<VendorRegistrations />} />
                <Route path="vendor-locations" element={<VendorLocationsAdmin />} />
                <Route path="vendor-performance" element={<VendorPerformance />} />
                <Route path="vendor-subscriptions" element={<VendorSubscriptions />} />
                <Route path="dispatch-queue" element={<DispatchQueue />} />
                <Route path="disputes" element={<DisputesQueue />} />

                {/* Reports */}
                <Route path="reports">
                  <Route path="revenue" element={<RevenueReport />} />
                  <Route path="vendor-sla" element={<VendorSlaReport />} />
                </Route>

                {/* Maintenance Routes */}
                <Route path="maintenance">
                  <Route path="roles" element={<RolesPermissions />} />
                  <Route path="common-codes" element={<CommonCodes />} />
                  <Route path="cause-of-calls" element={<CauseOfCalls />} />
                  <Route path="diagnostics" element={<Diagnostics />} />
                  <Route path="dealers" element={<DealerMaintenance />} />
                  <Route path="client-service-rates" element={<ClientServiceRates />} />
                  <Route path="regions" element={<Regions />} />
                  <Route path="scheme-service-setup" element={<SchemeServiceSetup />} />
                  <Route path="service-statuses" element={<ServiceStatuses />} />
                  <Route path="sms-templates" element={<SmsTemplates />} />
                </Route>
              </Route>
            </Routes>
          </Router>
          </GoogleMapsProvider>
          </ConvexProvider>
          </DialogProvider>
        </AuthProvider>
      </BrandingProvider>
    </ErrorBoundary>
  );
}

export default App;